import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Shuffle, Heart, Check, AlertTriangle, Grid3x3, ImageDown } from 'lucide-react';
import StoryExportModal from '../components/tool/StoryExportModal';
import { BIO_CATEGORIES, BIO_TEMPLATES, BIO_TEMPLATES_FR, IG_BIO_LIMIT } from '../data/bios';
import { BIO_T } from '../data/generatorsI18n';
import { cursiveStyles } from '../data/styles';
import { copyText } from '../lib/helpers';
import Faq from '../components/Faq';
import { readPreference, writePreference } from '../lib/consent';
import { useConsent } from '../components/consent/ConsentProvider';

const FAV_KEY = 'ctg_bio_favs';
const NAME_STYLES = cursiveStyles.filter((s) => ['cursive-bold', 'cursive-normal', 'italic-serif', 'bold-italic', 'fraktur', 'bold-fraktur', 'double-struck', 'sans-bold-italic', 'small-caps', 'serif-bold'].includes(s.id));

const fill = (lines, vals, nameStyled, defaults) =>
  lines.map((l) => l
    .replace(/\{name\}/g, nameStyled)
    .replace(/\{city\}/g, vals.city || defaults.city)
    .replace(/\{job\}/g, vals.job || defaults.job)
    .replace(/\{hobby\}/g, vals.hobby || defaults.hobby)).join('\n');

// Instagram counts in UTF-16 code units
const igLength = (s) => s.length;

function ProfileMock({ bio, name, L }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_18px_40px_-18px_rgba(15,23,42,0.35)] overflow-hidden" data-testid="bio-profile-mock">
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-5">
          <div className="w-[78px] h-[78px] rounded-full p-[3px] bg-[conic-gradient(#f9ce34,#ee2a7b,#6228d7,#f9ce34)] shrink-0">
            <div className="w-full h-full rounded-full bg-[var(--paper2)] border-[3px] border-white flex items-center justify-center text-2xl font-garamond text-[var(--brand)]">{(name || 'Y')[0].toUpperCase()}</div>
          </div>
          <div className="flex-1 grid grid-cols-3 text-center">
            {L.stats.map(([v, l]) => (
              <div key={l}><div className="font-semibold text-[15px] text-slate-900">{v}</div><div className="text-[11.5px] text-slate-500">{l}</div></div>
            ))}
          </div>
        </div>
        <div className="mt-3 text-[13px] font-semibold text-slate-900">{(name || 'yourname').toLowerCase().replace(/\s+/g, '.')}</div>
        <div className="text-[13.5px] text-slate-800 whitespace-pre-line leading-[1.45] mt-0.5 break-words" data-testid="bio-preview-text">{bio}</div>
        <div className="grid grid-cols-2 gap-1.5 mt-4">
          <div className="text-center text-[12.5px] font-semibold bg-slate-100 rounded-lg py-1.5">{L.editProfile}</div>
          <div className="text-center text-[12.5px] font-semibold bg-slate-100 rounded-lg py-1.5">{L.shareProfile}</div>
        </div>
      </div>
      <div className="border-t border-slate-100 flex justify-center py-2 text-slate-700"><Grid3x3 className="w-4 h-4" /></div>
      <div className="grid grid-cols-3 gap-[2px]">
        {['#f3e3cf', '#e9d5c0', '#f7ece0', '#e6dccf', '#f1e0d6', '#ede4d8'].map((c) => <div key={c} className="aspect-square" style={{ background: c }} />)}
      </div>
    </div>
  );
}

export default function BioGenerator({ lang = 'en' }) {
  const { preferencesAllowed } = useConsent();
  const L = BIO_T[lang] || BIO_T.en;
  const [vals, setVals] = useState({ name: '', city: '', job: '', hobby: '' });
  const [styleId, setStyleId] = useState('cursive-bold');
  const [cat, setCat] = useState('all');
  const [tplLang, setTplLang] = useState(lang);
  const TEMPLATES = tplLang === 'fr' ? BIO_TEMPLATES_FR : BIO_TEMPLATES;
  const [selected, setSelected] = useState(tplLang === 'fr' ? BIO_TEMPLATES_FR[0].id : BIO_TEMPLATES[0].id);
  const [favs, setFavs] = useState([]);
  const [onlyFavs, setOnlyFavs] = useState(false);
  const [copied, setCopied] = useState(null);
  const [storyOpen, setStoryOpen] = useState(false);

  useEffect(() => {
    if (!preferencesAllowed) {
      setFavs([]);
      setOnlyFavs(false);
      return;
    }
    const saved = readPreference(FAV_KEY, []);
    setFavs(Array.isArray(saved) ? saved : []);
    const savedLang = readPreference('ctg_bio_lang', lang);
    const restoredLang = savedLang === 'fr' ? 'fr' : savedLang === 'en' ? 'en' : lang;
    setTplLang(restoredLang);
    setSelected((restoredLang === 'fr' ? BIO_TEMPLATES_FR : BIO_TEMPLATES)[0].id);
  }, [preferencesAllowed, lang]);

  const style = NAME_STYLES.find((s) => s.id === styleId);
  const nameStyled = style.fn(vals.name || L.defaults.name);
  const list = useMemo(() => TEMPLATES.filter((t) => (cat === 'all' || t.cat === cat) && (!onlyFavs || favs.includes(t.id))), [TEMPLATES, cat, onlyFavs, favs]);
  const current = TEMPLATES.find((t) => t.id === selected) || TEMPLATES[0];
  const currentBio = fill(current.lines, vals, nameStyled, L.defaults);
  const len = igLength(currentBio);

  const switchLang = (l) => {
    setTplLang(l);
    writePreference('ctg_bio_lang', l);
    const src = l === 'fr' ? BIO_TEMPLATES_FR : BIO_TEMPLATES;
    const idx = Math.max(0, TEMPLATES.findIndex((t) => t.id === current.id));
    setSelected((src[idx] || src[0]).id);
  };

  const set = (k) => (e) => setVals({ ...vals, [k]: e.target.value.slice(0, 30) });
  const toggleFav = (id) => {
    const next = favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id];
    setFavs(next);
    writePreference(FAV_KEY, next);
  };
  const doCopy = async (id, text) => { if (!await copyText(text, L.copiedToast)) return; setCopied(id); setTimeout(() => setCopied(null), 1300); };
  const random = () => { const t = TEMPLATES[Math.floor(Math.random() * TEMPLATES.length)]; setSelected(t.id); setStyleId(NAME_STYLES[Math.floor(Math.random() * NAME_STYLES.length)].id); };

  return (
    <div data-testid="bio-page">
      <section className="content-narrow pt-8 pb-5 text-center fade-up">
        <h1 className="font-hand text-[30px] md:text-[38px] leading-[1.35] text-slate-900" data-testid="page-title">{L.h1}</h1>
        <p className="text-[15px] text-slate-600 mt-2 max-w-[640px] mx-auto">{L.sub}</p>
      </section>

      <div className="tool max-w-[1060px] mx-auto px-4 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5 items-start">
        <div className="space-y-3">
          <div className="tool-card cream p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {L.fields.map(([k, l, ph]) => (
                <label key={k} className="block">
                  <span className="upper-label">{l}{k === 'name' && <span className="text-[var(--gold)]"> *</span>}</span>
                  <input value={vals[k]} onChange={set(k)} placeholder={ph}
                    className="mt-1 w-full rounded-md border border-[var(--line)] bg-white px-3 py-2 text-[15px] outline-none focus:border-[var(--gold2)] focus:ring-2 focus:ring-[var(--gold2)]/15"
                    data-testid={`bio-input-${k}`} />
                </label>
              ))}
            </div>
          </div>

          <div className="tool-card p-4">
            <div className="upper-label flex items-center gap-2 mb-3"><span className="w-4 h-px bg-[var(--gold2)]" />{L.nameStyle}</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {NAME_STYLES.map((s) => (
                <button type="button" aria-pressed={styleId === s.id} key={s.id} className={`spill ${styleId === s.id ? 'active' : ''}`} onClick={() => setStyleId(s.id)} data-testid={`bio-style-${s.id}`}>
                  <div className="spill-name">{s.name}</div>
                  <div className="spill-prev">{s.fn(vals.name || L.defaults.name)}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="upper-label">{L.tplLang}</span>
            <div className="inline-flex rounded-full border border-[var(--line)] bg-white p-0.5" data-testid="bio-lang-toggle">
              {[['fr', '🇫🇷 Français'], ['en', '🇺🇸 English']].map(([k, l]) => (
                <button key={k} onClick={() => switchLang(k)} className={`px-3 py-1 text-[12.5px] rounded-full transition-colors ${tplLang === k ? 'bg-[var(--gold)] text-white' : 'text-[var(--ink2)] hover:bg-[var(--paper2)]'}`} data-testid={`bio-lang-${k}`}>{l}</button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {BIO_CATEGORIES.map((c) => (
              <button key={c.id} className={`pill-btn ${cat === c.id ? 'active' : ''}`} onClick={() => setCat(c.id)} data-testid={`bio-cat-${c.id}`}>{L.cats[c.id]}</button>
            ))}
            <button className={`pill-btn inline-flex items-center gap-1 ${onlyFavs ? 'active' : ''}`} onClick={() => setOnlyFavs(!onlyFavs)} data-testid="bio-only-favs">
              <Heart className={`w-3 h-3 ${onlyFavs ? 'fill-current' : ''}`} />{L.favs} ({favs.length})
            </button>
            <button className="pill-btn inline-flex items-center gap-1 ml-auto" onClick={random} data-testid="bio-random"><Shuffle className="w-3 h-3" />{L.random}</button>
          </div>

          {list.length === 0 ? (
            <div className="tool-card p-6 text-center text-[13px] text-[var(--ink4)] italic" data-testid="bio-empty">{L.emptyFavs}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {list.map((t) => {
                const bio = fill(t.lines, vals, nameStyled, L.defaults);
                const over = igLength(bio) > IG_BIO_LIMIT;
                const fav = favs.includes(t.id);
                return (
                  <div key={t.id} className={`spill !p-4 flex flex-col ${selected === t.id ? 'active' : ''}`} data-testid={`bio-template-${t.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="spill-name">{L.cats[t.cat]}</span>
                      <button onClick={(e) => { e.stopPropagation(); toggleFav(t.id); }} aria-label={L.favs} className="p-1 rounded hover:bg-white/70" data-testid={`bio-fav-${t.id}`}>
                        <Heart className={`w-4 h-4 transition-colors ${fav ? 'fill-[#e5484d] text-[#e5484d]' : 'text-[var(--ink4)]'}`} />
                      </button>
                    </div>
                    <button type="button" aria-pressed={selected === t.id} onClick={() => setSelected(t.id)} data-testid={`bio-select-${t.id}`} className="block w-full text-left text-[14.5px] text-[var(--ink)] whitespace-pre-line leading-[1.5] my-2.5 flex-1 break-words">{bio}</button>
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] ${over ? 'text-[#c2410c] font-semibold' : 'text-[var(--ink4)]'}`}>{igLength(bio)}/{IG_BIO_LIMIT}</span>
                      <button className={`copy-btn inline-flex items-center gap-1 ${copied === t.id ? 'done' : ''}`} onClick={(e) => { e.stopPropagation(); doCopy(t.id, bio); }} data-testid={`bio-copy-${t.id}`}>
                        {copied === t.id ? <><Check className="w-3 h-3" />{L.copied}</> : <><Copy className="w-3 h-3" />{L.copy}</>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-[96px] space-y-3">
          <div className="upper-label">{L.preview}</div>
          <ProfileMock bio={currentBio} name={vals.name} L={L} />
          <div className={`flex items-center gap-2 text-[12.5px] rounded-md px-3 py-2 border ${len > IG_BIO_LIMIT ? 'bg-[#fff4ed] border-[#fed7aa] text-[#c2410c]' : 'bg-[#f0f9f1] border-[#cdebd2] text-[#2f7d3e]'}`} data-testid="bio-length-indicator">
            {len > IG_BIO_LIMIT ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            {len} / {IG_BIO_LIMIT} {L.chars} {len > IG_BIO_LIMIT ? L.lenOver : L.lenOk}
          </div>
          <button className="btn-gold w-full inline-flex items-center justify-center gap-2 !py-2.5" onClick={() => doCopy('main', currentBio)} data-testid="bio-copy-main">
            <Copy className="w-4 h-4" />{L.copyBio}
          </button>
          <button className="btn-outline w-full inline-flex items-center justify-center gap-2 !py-2.5" onClick={() => setStoryOpen(true)} data-testid="bio-story-btn">
            <ImageDown className="w-4 h-4" />{L.storyBtn}
          </button>
          <StoryExportModal open={storyOpen} onOpenChange={setStoryOpen} bio={currentBio} name={vals.name} lang={lang} />
          <p className="text-[11.5px] text-[var(--ink4)] text-center">{L.moreStyles} <Link to={L.moreStylesTo} className="text-[var(--gold)] underline">{L.moreStylesLink}</Link>.</p>
        </aside>
      </div>

      <section className="content-narrow py-12 prose-site">
        <h2>{L.howTitle}</h2>
        <ul>
          {L.howSteps.map(([b, d]) => <li key={b}><b>{b}</b> : {d}</li>)}
        </ul>
        <p>{L.tip}</p>
      </section>
      <Faq items={L.faq} title={L.faqTitle} />
    </div>
  );
}
